import { 
  DeepgramResponse, 
  SpeakerSegment, 
  SpeakerLabels, 
  TranscriptionUtterance,
  TranscriptionWord 
} from '../types';

export class TranscriptProcessor {
  
  static formatTimestamp(seconds: number): string {
    if (seconds < 0 || !isFinite(seconds)) {
      return '[00:00:00]';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `[${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}]`;
  }

  static extractSpeakerSegments(transcriptionData: DeepgramResponse): Map<number, SpeakerSegment[]> {
    const speakerSegments = new Map<number, SpeakerSegment[]>();
    
    const words: TranscriptionWord[] = (transcriptionData.results?.channels || []).flatMap(
        channel => channel.alternatives.flatMap(alt => alt.words)
    );

    if (words.length === 0) {
        return speakerSegments;
    }

    let currentSegment: SpeakerSegment | null = null;

    for (const word of words) {
        if (!word.word?.trim() || word.speaker === undefined) continue;

        const wordText = word.punctuated_word || word.word;

        if (currentSegment && currentSegment.speaker === word.speaker) {
            // Continue the current segment
            currentSegment.transcript += ` ${wordText}`;
            currentSegment.end = word.end;
        } else {
            // End the previous segment and push it
            if (currentSegment) {
                if (!speakerSegments.has(currentSegment.speaker)) {
                    speakerSegments.set(currentSegment.speaker, []);
                }
                speakerSegments.get(currentSegment.speaker)!.push(currentSegment);
            }

            // Start a new segment for the new speaker
            currentSegment = {
                speaker: word.speaker,
                start: word.start,
                end: word.end,
                transcript: wordText.trim(),
            };
        }
    }

    // Add the final segment
    if (currentSegment) {
        if (!speakerSegments.has(currentSegment.speaker)) {
            speakerSegments.set(currentSegment.speaker, []);
        }
        speakerSegments.get(currentSegment.speaker)!.push(currentSegment);
    }
    
    // Sort segments within each speaker group by start time.
    speakerSegments.forEach((segments) => {
        segments.sort((a, b) => a.start - b.start);
    });

    return speakerSegments;
  }

  static getDistinctSpeakers(speakerSegments: Map<number, SpeakerSegment[]>): number[] {
    return Array.from(speakerSegments.keys())
      .filter(speaker => speaker !== undefined)
      .sort((a, b) => a - b);
  }

  static generateFormattedTranscript(
    transcriptionData: DeepgramResponse,
    speakerLabels: SpeakerLabels,
    timeInterval: number = 15
  ): string {
    const words = transcriptionData.results?.channels?.[0]?.alternatives?.[0]?.words;
    if (!words || words.length === 0) {
        return transcriptionData.results?.channels?.[0]?.alternatives?.[0]?.transcript?.trim() || 'No speech detected.';
    }

    let result: string[] = [];
    let currentLine: string[] = [];
    let lastSpeaker: number | undefined = undefined;
    let lastWordEnd = 0;
    
    // Set the first timestamp to be inserted at the 'timeInterval' mark.
    let nextTimestamp = timeInterval > 0 ? timeInterval : Infinity;

    const flushLine = () => {
        if (currentLine.length > 0) {
            result.push('  ' + currentLine.join(' '));
            currentLine = [];
        }
    };

    for (const word of words) {
        if (word.speaker === undefined || !word.word?.trim()) continue;

        // Handle speaker change
        if (word.speaker !== lastSpeaker) {
            flushLine();
            if (result.length > 0) result.push(''); // Blank line between speakers
            const speakerName = speakerLabels[word.speaker] || `Speaker ${word.speaker}`;
            result.push(`${speakerName} ${this.formatTimestamp(word.start)}:`);
            lastSpeaker = word.speaker;
        }

        // Handle periodic timestamps
        // Check if the gap between words is large enough to insert a timestamp
        const silenceDuration = word.start - lastWordEnd;
        if (timeInterval > 0 && silenceDuration > 2.0 && word.start > nextTimestamp) {
             flushLine();
             result.push(''); // Blank line before timestamp
             // Insert all timestamps that were missed during the silence
             while (word.start > nextTimestamp) {
                 result.push(`  ${this.formatTimestamp(nextTimestamp)}`);
                 nextTimestamp += timeInterval;
             }
             result.push(''); // Blank line after timestamp
        }
        
        // Add the word and update the end time
        currentLine.push(word.punctuated_word || word.word);
        lastWordEnd = word.end;

        // This handles timestamps that fall *within* continuous speech.
        if (timeInterval > 0 && lastWordEnd >= nextTimestamp) {
            flushLine();
            result.push('');
            while(lastWordEnd >= nextTimestamp) {
                result.push(`  ${this.formatTimestamp(nextTimestamp)}`);
                nextTimestamp += timeInterval;
            }
            result.push('');
        }
    }

    flushLine(); // Flush any remaining words

    return result.join('\n');
  }

  static getSpeakerAudioSegments(
    speakerSegments: Map<number, SpeakerSegment[]>, 
    speakerId: number
  ): { start: number; end: number }[] {
    const segments = speakerSegments.get(speakerId) || [];
    return segments.map(segment => ({
      start: segment.start,
      end: segment.end
    }));
  }

  static generateSampleSegment(
    audioSegments: { start: number; end: number }[],
    sampleDuration: number = 5.0
  ): { start: number; duration: number } | null {
    if (audioSegments.length === 0) {
      return null;
    }
    
    // Find segments long enough for the sample duration
    const eligibleSegments = audioSegments.filter(
      segment => (segment.end - segment.start) >= sampleDuration
    );
    
    if (eligibleSegments.length > 0) {
      // Choose a random eligible segment
      const randomSegment = eligibleSegments[Math.floor(Math.random() * eligibleSegments.length)];
      const maxStartTime = randomSegment.end - sampleDuration;
      const startTime = Math.random() * (maxStartTime - randomSegment.start) + randomSegment.start;
      
      return {
        start: startTime,
        duration: sampleDuration
      };
    } else {
      // Use the longest segment available
      const longestSegment = audioSegments.reduce((longest, current) => 
        (current.end - current.start) > (longest.end - longest.start) ? current : longest
      );
      
      const actualDuration = Math.min(sampleDuration, longestSegment.end - longestSegment.start);
      
      if (actualDuration < 0.1) {
        return null; // Too short to be useful
      }
      
      return {
        start: longestSegment.start,
        duration: actualDuration
      };
    }
  }
}

export default TranscriptProcessor; 